/** Presentation policy based on tenant entitlements and configured channels. */
export function buildTenantCapabilities({ enabledModules = [], moduleConfigs = {}, channels = {}, experienceMode, fieldOperationModules = [] } = {}) {
  const modules = enabledModules.filter(key => moduleConfigs[key]?.channels?.tenantPortal !== false);
  const marketplace = experienceMode !== 'direct_only' && channels.marketplace === true;
  const configuredFieldModules = new Set(fieldOperationModules ?? []);
  const fieldOperations = modules.includes('home_services') || modules.some(module => configuredFieldModules.has(module));
  return {
    modules, marketplace, fieldOperations,
    bookingOnly: modules.length === 1 && modules[0] === 'booking',
    calendar: modules.includes('booking') || modules.includes('online_services'),
    shop: modules.includes('shop'),
    services: modules.some(key => ['booking', 'home_services', 'online_services'].includes(key)),
    nativeApps: ['customerApp', 'providerApp', 'operationsApp'].some(key => channels[key] === true),
    directBusinesses: experienceMode === 'direct_only',
  };
}

const FIELD_PATHS = ['/dispatchers', '/maps/orders', '/maps/providers', '/maps/nearby', '/providers/status'];
const NATIVE_PATHS = ['/app-versions', '/mobile-releases', '/settings/google-play'];
export function isTenantPathRelevant(path, capabilities) {
  const clean = path.split(/[?#]/, 1)[0].replace(/\/$/, '') || '/';
  const under = prefix => clean === prefix || clean.startsWith(prefix + '/');
  if (FIELD_PATHS.some(under)) return capabilities.fieldOperations === true;
  if (NATIVE_PATHS.some(under)) return capabilities.nativeApps === true;
  if (under('/branding/sliders')) return capabilities.nativeApps === true || capabilities.marketplace === true;
  if (under('/services')) return capabilities.services === true;
  if (under('/products') || under('/shop-orders')) return capabilities.shop === true;
  return true;
}

export function isProviderDetailTabRelevant(tab, capabilities, providerModules) {
  const modules = providerModules?.length ? providerModules.filter(key => capabilities.modules.includes(key)) : capabilities.modules;
  if (tab === 'products') return modules.includes('shop');
  if (tab === 'bookingSlots') return modules.includes('booking') || modules.includes('online_services');
  if (tab === 'services') return modules.some(key => key !== 'shop');
  // Shops opt into their own delivery team from business settings first.
  if (tab === 'dispatchConfig') return modules.includes('home_services') || capabilities.fieldOperations && modules.includes('shop');
  if (tab === 'offers' || tab === 'credits') return modules.some(key => key !== 'shop');
  if (tab === 'locations') return capabilities.fieldOperations && modules.some(key => key === 'home_services' || key === 'shop');
  return true;
}

export function isTenantJobRelevant(jobKey, capabilities) {
  if (['provider-location-reminder', 'dispatch-sla-watch'].includes(jobKey)) return capabilities.fieldOperations === true;
  if (jobKey === 'abandoned-cart-reminder') return capabilities.shop === true;
  if (['booking-reminder', 'order-auto-close', 'order-status-reminder', 'quotation-offer-reminder'].includes(jobKey)) return capabilities.services === true;
  return true;
}
